import type React from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useSpec } from '../../contexts/SpecContext';
import { SourceCardItem } from './SourceCardItem';
import styles from './VulnerabilityDetail.module.css';

interface OverviewTabProps {
  vulnerability: any;
  linkedTest: any;
  linkedHypothesis: any;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ vulnerability, linkedTest, linkedHypothesis }) => {
  const { activeSpec } = useSpec();
  const schemaId = vulnerability.schema_id || activeSpec?.id;
  const hasLinkedData = linkedTest || linkedHypothesis;

  return (
    <div>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Details</h3>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <div className={styles.infoLabel}>Vulnerability Type</div>
            <div className={styles.infoValue}>{vulnerability.vulnerability_type}</div>
          </div>
          <div className={styles.infoItem}>
            <div className={styles.infoLabel}>Endpoint</div>
            <div className={styles.infoValue}>
              {vulnerability.http_method} {vulnerability.endpoint}
            </div>
          </div>
          <div className={styles.infoItem}>
            <div className={styles.infoLabel}>Parameter</div>
            <div className={styles.infoValue}>{vulnerability.parameter_name || 'N/A'}</div>
          </div>
          <div className={styles.infoItem}>
            <div className={styles.infoLabel}>CWE ID</div>
            <div className={styles.infoValue}>{vulnerability.cwe_id || 'N/A'}</div>
          </div>
          <div className={styles.infoItem}>
            <div className={styles.infoLabel}>OWASP Category</div>
            <div className={styles.infoValue}>{vulnerability.owasp_category || 'N/A'}</div>
          </div>
          <div className={styles.infoItem}>
            <div className={styles.infoLabel}>First Found</div>
            <div className={styles.infoValue}>{new Date(vulnerability.first_found_date).toLocaleString()}</div>
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Description</h3>
        <p className={styles.description}>{vulnerability.description}</p>
      </div>

      {hasLinkedData && (
        <div className={styles.sourceSection}>
          <h3 className={styles.sectionTitle}>Source</h3>
          <div className={styles.sourceCards}>
            {linkedHypothesis && (
              <SourceCardItem
                type="Hypothesis"
                title={
                  linkedHypothesis.content?.vulnerability ||
                  linkedHypothesis.content?.strategy ||
                  linkedHypothesis.filename
                }
                meta={[
                  linkedHypothesis.content?.strategy && `Strategy: ${linkedHypothesis.content.strategy}`,
                  linkedHypothesis.filename,
                ]
                  .filter(Boolean)
                  .join(' \u00B7 ')}
                fileExists={linkedHypothesis.file_exists}
                linkTo={
                  linkedHypothesis.file_exists && schemaId
                    ? `/hypotheses/${schemaId}/${linkedHypothesis.filename}`
                    : undefined
                }
              />
            )}
            {linkedTest && (
              <SourceCardItem
                type="Test"
                title={linkedTest.filename}
                meta={linkedTest.test_id ? `Test ID: ${linkedTest.test_id}` : undefined}
                fileExists={linkedTest.file_exists}
                linkTo={linkedTest.file_exists && schemaId ? `/tests/${schemaId}/${linkedTest.filename}` : undefined}
              />
            )}
          </div>
        </div>
      )}

      {!!vulnerability.is_false_positive && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>False Positive Information</h3>
          <div className={styles.infoItem} style={{ marginBottom: '1rem' }}>
            <div className={styles.infoLabel}>Reason</div>
            <div className={styles.infoValue}>{vulnerability.false_positive_reason}</div>
          </div>
          {vulnerability.false_positive_summary && (
            <div className={styles.infoItem}>
              <div className={styles.infoLabel}>AI-Generated Summary</div>
              <div className={styles.infoValue}>{vulnerability.false_positive_summary}</div>
            </div>
          )}
        </div>
      )}

      {vulnerability.proof_of_concept && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Proof of Concept</h3>
          <div className={styles.codeBlock}>
            <SyntaxHighlighter language="bash" style={vscDarkPlus} customStyle={{ fontSize: '0.875rem' }}>
              {vulnerability.proof_of_concept}
            </SyntaxHighlighter>
          </div>
        </div>
      )}
    </div>
  );
};
